import { ArrowRight, Phone, Shield, Home, MapPin } from "lucide-react";

const scrollTo = (id) => {
  const el = document.querySelector(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const streams = [
  "NDIS",
  "Support at Home",
  "Transition Care Program (TCP)",
  "DVA",
  "CTP",
];

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 sm:pt-32 lg:pt-40 pb-20 sm:pb-28 overflow-hidden grain"
    >
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(1200px 600px at 85% -10%, rgba(163,196,211,0.35), transparent 60%), radial-gradient(900px 500px at -10% 10%, rgba(110,156,136,0.22), transparent 60%), linear-gradient(180deg, #FAFAF7 0%, #FFFFFF 100%)",
        }}
      />

      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7 reveal">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/80 backdrop-blur border border-[#6E9C88]/25 px-4 py-1.5 text-xs font-medium text-[var(--ew-accent)]">
              <span className="h-2 w-2 rounded-full bg-[var(--ew-sage)] animate-pulse" />
              Accepting participant and provider referrals
            </div>

            <h1
              data-testid="hero-title"
              className="font-heading font-medium mt-6 text-[2.25rem] sm:text-5xl lg:text-[3.75rem] leading-[1.05] tracking-[-0.015em] text-[var(--ew-text)]"
            >
              Community Nursing for{" "}
              <span className="relative inline-block">
                <span className="relative z-10">Individuals</span>
                <span
                  aria-hidden
                  className="absolute left-0 right-0 bottom-1 h-3 sm:h-4 bg-[var(--ew-blue)]/40 rounded-full -z-0"
                />
              </span>{" "}
              &amp; Healthcare Partners
            </h1>

            <div className="mt-6 flex flex-wrap gap-x-2 gap-y-2 text-sm sm:text-base font-medium text-[var(--ew-accent)]">
              {streams.map((s, i) => (
                <span key={s} className="inline-flex items-center">
                  {s}
                  {i < streams.length - 1 && (
                    <span
                      aria-hidden
                      className="mx-2 text-[var(--ew-sage)]"
                    >
                      •
                    </span>
                  )}
                </span>
              ))}
            </div>

            <p
              data-testid="hero-subtitle"
              className="mt-6 text-base sm:text-lg text-[var(--ew-text-2)] leading-relaxed max-w-2xl"
            >
              Providing RN-led community nursing across Canberra and nearby
              NSW.
            </p>

            <p className="mt-5 text-base text-[var(--ew-text-2)] leading-relaxed max-w-2xl">
              Everwell Community Nursing Pty Ltd provides direct nursing support
              to NDIS participants and flexible subcontracted clinical services
              to healthcare, aged care, rehabilitation and community care
              organisations.
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                data-testid="hero-referral-btn"
                onClick={() => scrollTo("#referrals")}
                className="btn-primary"
              >
                Make a Referral <ArrowRight className="h-4 w-4" strokeWidth={2} />
              </button>
              <button
                data-testid="hero-subcontract-btn"
                onClick={() => scrollTo("#referrals")}
                className="btn-secondary"
              >
                Discuss Subcontracting
              </button>
            </div>

            <div className="mt-10 flex flex-wrap gap-x-6 gap-y-3">
              {[
                { icon: Shield, text: "Registered Nurse led" },
                { icon: Home, text: "Direct & subcontracted nursing" },
                { icon: MapPin, text: "Canberra & nearby NSW" },
              ].map((t, i) => (
                <div
                  key={i}
                  data-testid={`hero-trust-${i}`}
                  className="inline-flex items-center gap-2 text-sm text-[var(--ew-text-2)]"
                >
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[var(--ew-sage)]/12 text-[var(--ew-accent)]">
                    <t.icon className="h-4 w-4" strokeWidth={1.75} />
                  </span>
                  <span className="font-medium">{t.text}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative reveal reveal-delay-2">
            <div className="relative">
              <div className="absolute -top-6 -left-6 h-24 w-24 rounded-full bg-[var(--ew-blue)]/30 blur-2xl" />
              <div className="absolute -bottom-8 -right-6 h-36 w-36 rounded-full bg-[var(--ew-sage)]/30 blur-3xl" />

              <div className="relative rounded-[28px] overflow-hidden border border-[#6E9C88]/20 shadow-[0_20px_60px_rgba(46,80,65,0.14)]">
                <img
                  src="https://images.unsplash.com/photo-1765896387387-0538bc9f997e?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Registered nurse caring for an older person at home"
                  className="w-full h-[440px] sm:h-[520px] object-cover"
                  loading="eager"
                />
              </div>

              <div
                data-testid="hero-floating-card"
                className="absolute -left-4 sm:-left-8 bottom-8 bg-white rounded-2xl border border-[#6E9C88]/20 shadow-[0_14px_40px_rgba(46,80,65,0.12)] p-4 sm:p-5 w-[260px] sm:w-[290px]"
              >
                <div className="flex items-center gap-3">
                  <div className="icon-badge">
                    <Shield className="h-5 w-5" strokeWidth={1.75} />
                  </div>
                  <div>
                    <p className="font-heading text-sm font-semibold text-[var(--ew-text)]">
                      Continuity of clinician
                    </p>
                    <p className="text-xs text-[var(--ew-text-muted)]">
                      the same RN, wherever possible
                    </p>
                  </div>
                </div>
                <div className="mt-3 h-px bg-[#6E9C88]/15" />
                <p className="mt-3 text-xs text-[var(--ew-text-2)] leading-relaxed">
                  Proactive nursing to catch issues early and keep clients
                  stable at home.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
